<!--
	SegmentedControl: pick one of a few options. Options without `href` are native radios in a
	fieldset (arrow keys for free); options with `href` render as links with `aria-current`.
-->
<script lang="ts">
	type Option = { value: string; label: string; href?: string };

	interface Props {
		options: Option[];
		value: string;
		/** Group name: the fieldset legend, or the nav landmark label for links. */
		label: string;
		onchange?: (value: string) => void;
		size?: 'sm' | 'md';
		/** Disables the radios; ignored for links. */
		disabled?: boolean;
	}

	const { options, value, label, onchange, size = 'md', disabled = false }: Props = $props();

	const name = $props.id();
	const links = $derived(options.every((option) => option.href));
</script>

{#if links}
	<nav class="segmented {size}" aria-label={label}>
		<div class="options">
			{#each options as option (option.value)}
				<a
					class="option"
					href={option.href}
					aria-current={option.value === value ? 'page' : undefined}>
					{option.label}
				</a>
			{/each}
		</div>
	</nav>
{:else}
	<fieldset class="segmented {size}" {disabled}>
		<legend>{label}</legend>
		<div class="options">
			{#each options as option (option.value)}
				<label class="option">
					<input
						type="radio"
						{name}
						value={option.value}
						checked={option.value === value}
						onchange={() => onchange?.(option.value)} />
					{option.label}
				</label>
			{/each}
		</div>
	</fieldset>
{/if}

<style>
	/* Sized to its content, so a click-through wrapper (e.g. the map's view switch) stays click-through around it. */
	.segmented {
		display: inline-block;
		border: 0;
		padding: 0;

		legend {
			margin-bottom: var(--space-2);
			padding: 0;
			font-weight: var(--weight-regular);
		}
	}

	/* One bordered strip; only the outer corners are rounded. */
	.options {
		display: inline-flex;
		flex-wrap: wrap;
		border: 1px solid var(--border-strong);
		border-radius: var(--radius);
		background-color: var(--surface);
		overflow: hidden;
	}

	.option {
		display: inline-flex;
		position: relative;
		justify-content: center;
		align-items: center;
		cursor: pointer;
		padding-inline: var(--space-4);
		min-height: var(--height-button);
		color: var(--text-color);
		font-weight: var(--weight-semibold);
		font-size: var(--font-size-sm);
		text-decoration: none;
		user-select: none;

		& + .option {
			border-left: 1px solid var(--border-strong);
		}

		&:hover {
			background-color: var(--hover-surface);
		}

		&:has(:checked),
		&[aria-current='page'] {
			background-color: var(--selected-surface);
			color: var(--selected-text);
		}

		&:has(:focus-visible),
		&:focus-visible {
			outline: 2px solid var(--focus-ring);
			outline-offset: -4px;
		}

		/* Visually hidden but still focusable, so the label carries the focus ring. */
		input {
			position: absolute;
			opacity: 0;
			inset: 0;
			margin: 0;
			cursor: pointer;
			width: 100%;
			height: 100%;
		}
	}

	.sm .option {
		padding-inline: var(--space-3);
		min-height: var(--height-button-sm);
	}

	@media (forced-colors: active) {
		.option:has(:checked) {
			outline: 2px solid Highlight;
			outline-offset: -2px;
		}
	}
</style>
